<template>
  <div>

    <!-- Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">My Assets</h5>
        <p class="page-sub">View all assets assigned to you by your company</p>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-table">
      <div class="sk-row-head"></div>
      <div v-for="n in 4" :key="n" class="sk-row"></div>
    </div>

    <!-- Empty state -->
    <div v-else-if="!allocations.length" class="empty-box">
      <div class="empty-illustration">
        <font-awesome-icon icon="box-open" class="empty-icon" />
      </div>
      <p class="empty-title">No assets assigned</p>
      <p class="empty-sub">You have no assets assigned to you at the moment.</p>
    </div>

    <!-- Table -->
    <div v-else class="table-card">
      <table class="data-table">
        <thead>
          <tr>
            <th>Asset</th>
            <th>Category</th>
            <th>Asset No.</th>
            <th>Serial No.</th>
            <th>Assigned On</th>
            <th>Return Due</th>
            <th>Returned On</th>
            <th>Condition</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in allocations" :key="a.id" class="clickable-row" @click="viewDetail(a)">
            <td class="fw-600">{{ a.asset?.name || '—' }}</td>
            <td>{{ a.asset?.category?.name || '—' }}</td>
            <td>{{ a.asset?.asset_no || '—' }}</td>
            <td>{{ a.asset?.serial_no || '—' }}</td>
            <td>{{ fmtDate(a.allocated_at) }}</td>
            <td>{{ fmtDate(a.return_due_date) }}</td>
            <td>{{ fmtDate(a.returned_at) }}</td>
            <td>
              <span v-if="a.condition_at_return" class="chip" :class="conditionClass(a.condition_at_return)">
                {{ conditionLabel(a.condition_at_return) }}
              </span>
              <span v-else>—</span>
            </td>
            <td>
              <span class="chip" :class="statusClass(a.status)">{{ statusLabel(a.status) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Detail Modal -->
    <div class="modal-overlay" v-if="detail.open" @click.self="detail.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head">
          <p class="modal-title">Asset Detail</p>
          <button class="modal-close" @click="detail.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" v-if="detail.item">
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Asset Name</span>
              <span class="detail-value fw-600">{{ detail.item.asset?.name || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Category</span>
              <span class="detail-value">{{ detail.item.asset?.category?.name || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Asset Number</span>
              <span class="detail-value">{{ detail.item.asset?.asset_no || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Serial Number</span>
              <span class="detail-value">{{ detail.item.asset?.serial_no || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Brand / Model</span>
              <span class="detail-value">{{ [detail.item.asset?.brand, detail.item.asset?.model_no].filter(Boolean).join(' / ') || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Location</span>
              <span class="detail-value">{{ detail.item.asset?.location || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Status</span>
              <span class="chip" :class="statusClass(detail.item.status)">{{ statusLabel(detail.item.status) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Condition at Return</span>
              <span v-if="detail.item.condition_at_return" class="chip" :class="conditionClass(detail.item.condition_at_return)">
                {{ conditionLabel(detail.item.condition_at_return) }}
              </span>
              <span v-else class="detail-value">—</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Assigned On</span>
              <span class="detail-value">{{ fmtDate(detail.item.allocated_at) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Return Due Date</span>
              <span class="detail-value">{{ fmtDate(detail.item.return_due_date) }}</span>
            </div>
            <div class="detail-item" v-if="detail.item.returned_at">
              <span class="detail-label">Returned On</span>
              <span class="detail-value">{{ fmtDate(detail.item.returned_at) }}</span>
            </div>
          </div>
          <div v-if="detail.item.notes" class="notes-box">
            <p class="notes-label">Assignment Notes</p>
            <p class="notes-content">{{ detail.item.notes }}</p>
          </div>
          <div v-if="detail.item.return_notes" class="review-note-box">
            <p class="notes-label">Return Notes</p>
            <p class="notes-content">{{ detail.item.return_notes }}</p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-primary-action" @click="detail.open = false">Close</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'MyAssetsPage',
  data() {
    return {
      loading: false,
      allocations: [],
      detail: { open: false, item: null },
    };
  },

  mounted() {
    this.fetchAssets();
  },

  methods: {
    async fetchAssets() {
      this.loading = true;
      try {
        const { data } = await axios.get('/hr/self-service/assets');
        this.allocations = data.data || [];
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load assets');
      } finally {
        this.loading = false;
      }
    },
    viewDetail(item) {
      this.detail = { open: true, item };
    },
    fmtDate(d) {
      return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    },
    statusLabel(s) {
      return { active: 'Active', returned: 'Returned' }[s] || s;
    },
    statusClass(s) {
      return { active: 'chip--green', returned: 'chip--gray' }[s] || 'chip--gray';
    },
    conditionLabel(c) {
      return { good: 'Good', damaged: 'Damaged', lost: 'Lost' }[c] || c;
    },
    conditionClass(c) {
      return { good: 'chip--green', damaged: 'chip--amber', lost: 'chip--red' }[c] || 'chip--gray';
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary-action:hover { background:#003b8e; }

.table-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:12px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .1s; }
.data-table tbody tr.clickable-row { cursor:pointer; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 14px; color:#1a2b45; vertical-align:middle; white-space:nowrap; }
.fw-600 { font-weight:600; }

.chip { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }
.chip--red    { background:#fee2e2; color:#dc2626; }
.chip--gray   { background:#f1f5f9; color:#64748b; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row-head,.sk-row { height:44px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
.sk-row-head { height:40px; background:#f8fafc; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.empty-box         { display:flex; flex-direction:column; align-items:center; padding:80px 20px; gap:14px; }
.empty-illustration{ width:96px; height:96px; border-radius:50%; background:#f0f7ff; display:flex; align-items:center; justify-content:center; }
.empty-icon        { font-size:40px; color:#004AAD; opacity:.5; }
.empty-title       { font-size:18px; font-weight:700; color:#1a2b45; margin:0; }
.empty-sub         { font-size:13px; color:#7a90b0; margin:0; text-align:center; max-width:380px; line-height:1.6; }

.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box     { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--wide { max-width:660px; }
.modal-head    { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title   { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close   { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; }
.modal-body    { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer  { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }

.detail-grid   { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.detail-item   { display:flex; flex-direction:column; gap:3px; }
.detail-label  { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; }
.detail-value  { font-size:14px; color:#1a2b45; }

.notes-box, .review-note-box { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:12px 14px; }
.review-note-box { background:#fff7ed; border-color:#fdba74; }
.notes-label   { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin:0 0 6px; }
.notes-content { font-size:13px; color:#1a2b45; margin:0; line-height:1.6; }
</style>
